import { useCallback } from 'react';
import { RatingBadge } from '../../ui/rating';
import { presetFor } from '@app/shared';
import { useAppApi } from '../../app/ApiProvider';
import { industryName } from '../../domain/catalog';
import { formatAmount } from '../../domain/datetime';
import { useAsync } from '../../hooks/useAsync';
import { useI18n } from '../../i18n/I18nProvider';
import { CtaBar, CtaButton } from '../../ui/Cta';
import { Avatar, IndustryTile } from '../../ui/controls';
import { INDUSTRY_ICON } from '../../ui/icons';
import { EmptyState, ErrorState, Loader } from '../../ui/feedback';
import { Screen, ScrollArea, Section, SectionTitle, TopBar } from '../../ui/Screen';

/**
 * Страница организации (ТЗ §14.1): услуги, адрес, отрасль.
 *
 * РЕЙТИНГА ЗДЕСЬ НЕТ, и это не пропуск. Публичный ответ `GET /api/businesses/:slug`
 * (`toBusinessPageDto`) оценок не содержит вовсе — ни средней, ни числа отзывов. Нарисовать
 * «★ 0.0» или бейдж «Новая» значило бы утверждать про организацию то, чего мы не знаем:
 * ноль читается как худшая оценка. Нужное поле описано в отчёте.
 *
 * Тап по услуге открывает запись сразу на выбранной услуге — так же, как в прототипе
 * (`pickSrv(i, 1)`): вести на пустой экран записи после того, как человек уже выбрал
 * услугу, значит заставить его выбирать дважды.
 */
export function BusinessScreen(props: {
  slug: string;
  onBack: () => void;
  onBook: (serviceId?: string) => void;
}): JSX.Element {
  const { t, lang } = useI18n();
  const api = useAppApi();

  const { state, reload } = useAsync(
    useCallback(() => api.client.getBusiness(props.slug), [api, props.slug]),
    [props.slug],
  );

  if (state.status === 'loading') {
    return (
      <Screen>
        <TopBar title={t('loading')} onBack={props.onBack} />
        <Loader />
      </Screen>
    );
  }

  if (state.status === 'failed') {
    return (
      <Screen>
        <TopBar title={t('errTitle')} onBack={props.onBack} />
        <ErrorState error={state.error} onRetry={reload} />
      </Screen>
    );
  }

  const { business, services, staff } = state.data;
  const preset = presetFor(business.type);
  const CoverIcon = INDUSTRY_ICON[business.type];
  const bookable = services.length > 0 && staff.length > 0;

  return (
    <Screen>
      <TopBar
        title={business.name}
        subtitle={business.address ?? t('addrNone')}
        onBack={props.onBack}
      />

      <ScrollArea bottomGap="cta">
        <div className="px-4 pb-4 pt-4 text-center">
          {/* Обложка. До 16 сентября 2026 здесь стоял фиолетово-синий градиент —
              наследство от палитры Telegram, снятой 10 сентября: на фирменном фоне он
              выглядел чужой заплатой.

              Замена — подложка `surface-2` с крупной полупрозрачной иконкой отрасли.
              Лайм сюда не идёт: это цвет действия, и широкая лаймовая полоса над кнопкой
              «Записаться» обесценила бы саму кнопку. Пустая же плашка читалась бы как
              не загрузившаяся картинка.

              Это МЕСТО ПОД ФОТОГРАФИЮ заведения (архив/CATALOG_IMAGES.md): когда появится
              загрузка, снимок ляжет в этот же прямоугольник, а вёрстка не шелохнётся. */}
          <div
            className="relative mb-[-34px] h-24 overflow-hidden rounded-[16px] bg-surface-2"
            aria-hidden="true"
          >
            <CoverIcon
              className="absolute -right-3 -top-4 text-ink opacity-[0.10]"
              size={120}
              strokeWidth={1.25}
            />
          </div>
          <div className="relative mx-auto w-fit rounded-[20px] border-[3px] border-bg">
            {/* Тот же приём, что в каталоге: частный мастер — человек, заведение — отрасль. */}
            {business.isSolo ? (
              <Avatar name={business.name} size="lg" />
            ) : (
              <IndustryTile type={preset.type} size="lg" />
            )}
          </div>
          <div className="mt-3 text-[21px] font-extrabold tracking-[-0.02em]">{business.name}</div>
          <div className="mt-1 text-[13.5px] text-ink-3">{industryName(preset, lang)}</div>
          <div className="mt-1">
            <RatingBadge sum={business.ratingSum} count={business.ratingCount} />
          </div>
          <div className="mt-1 text-[13.5px] text-ink-3">{business.address ?? t('addrNone')}</div>
        </div>

        <Section>
          <SectionTitle>{t('bizServices')}</SectionTitle>
          {services.length === 0 ? (
            <EmptyState>{t('emptyServices')}</EmptyState>
          ) : (
            services.map((service) => (
              <button
                key={service.id}
                type="button"
                onClick={() => props.onBook(service.id)}
                className="flex w-full items-center justify-between gap-3 border-b border-hair px-1 py-3.5 text-left last:border-b-0"
              >
                <div className="min-w-0">
                  <div className="text-[14.5px] font-semibold">
                    {lang === 'ru' ? service.titleRu : service.titleUz}
                  </div>
                  <div className="mt-0.5 text-[12px] text-ink-3">
                    {service.durationMin} {t('min')}
                  </div>
                </div>
                <div className="whitespace-nowrap text-[14px] font-bold text-accent">
                  {formatAmount(service.priceSum, lang)} {t('sum')}
                </div>
              </button>
            ))
          )}
        </Section>

        {staff.length === 0 ? (
          <Section>
            <EmptyState>{t('emptyStaff')}</EmptyState>
          </Section>
        ) : null}
      </ScrollArea>

      <CtaBar>
        <CtaButton onClick={() => props.onBook(undefined)} disabled={!bookable}>
          {t('ctaBook')}
        </CtaButton>
      </CtaBar>
    </Screen>
  );
}
